import { useState } from "react";
import { Link } from "react-router-dom";
import { PLANNER_PAGES } from "@/lib/products";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, ZoomIn } from "lucide-react";
import { PlannerPagePreview } from "@/components/product/PlannerPagePreview";

const ALL_SECTIONS_LABEL = "Different Section Preview";
const TABS = [ALL_SECTIONS_LABEL, ...PLANNER_PAGES.map((p) => p.label)];

const FAQS = [
  { q: "Is the planner dated?", a: "No. It is undated so you can start anytime and skip weeks without wasting pages." },
  { q: "What size options are available?", a: "Planner comes in B5 Premium size." },
  { q: "What binding options are available?", a: "We provide two finish options: Soft Paperback and Hard Binding, so you can choose the feel that works best for you." },
  { q: "Is there a digital version?", a: "Yes. The digital companion is included as complimentary access with every planner." },
];

export default function InsidePlanner() {
  const [tab, setTab] = useState(ALL_SECTIONS_LABEL);
  const visible = tab === ALL_SECTIONS_LABEL ? PLANNER_PAGES : PLANNER_PAGES.filter((p) => p.label === tab);
  const featuredPage = PLANNER_PAGES.find((p) => p.key === "weekly-reset") || PLANNER_PAGES[0];

  return (
    <div data-testid="inside-page">
      <section className="container-nc py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="overline mb-5">Inside The Life OS Planner</p>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-tight">
            Every page.
            <br />
            <span className="italic">By design.</span>
          </h1>
          <p className="mt-6 text-muted_brown max-w-md leading-relaxed">
            Explore real pages from the planner, from vision prompts and comeback planning
            to daily dashboards, reset pages, wellness trackers, and money control.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/product/life-os-planner" className="btn-primary">
              Buy the Planner <ArrowRight size={16} />
            </Link>
            <Link to="/product/life-os-planner" className="btn-outline">
              View Cover &amp; Specs
            </Link>
          </div>
        </div>
        <div className="rounded-2xl w-full aspect-[3/4] overflow-hidden border border-line shadow-medium bg-cream max-w-md lg:ml-auto">
          <PlannerPagePreview page={PLANNER_PAGES.find((p) => p.key === "monthly") || PLANNER_PAGES[0]} />
        </div>
      </section>

      <section className="border-y border-line bg-ivory">
        <div className="container-nc py-4 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`inside-tab-${t.replace(/ /g, "-").toLowerCase()}`}
                className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  tab === t ? "bg-espresso text-cream" : "text-muted_brown hover:text-espresso"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="container-nc py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {visible.map((p) => (
            <Dialog key={p.key}>
              <DialogTrigger asChild>
                <button data-testid={`page-preview-${p.key}`} className="group text-left">
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-ivory border border-line">
                    <PlannerPagePreview
                      page={p}
                      className="transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/20 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 bg-cream text-espresso rounded-full w-10 h-10 flex items-center justify-center transition-opacity">
                        <ZoomIn size={16} />
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="text-espresso font-serif text-lg">{p.label}</div>
                    <div className="text-xs text-muted_brown tracking-wider uppercase mt-1">
                      {p.pages} {p.pages === 1 ? "page" : "pages"} - Preview
                    </div>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl bg-cream border-line">
                <div className="aspect-[3/4] w-full rounded-lg overflow-hidden border border-line">
                  <PlannerPagePreview page={p} />
                </div>
                <div className="mt-2">
                  <h3 className="font-serif text-2xl text-espresso">{p.label}</h3>
                  <p className="text-sm text-muted_brown mt-1">
                    Real page preview from Not Just A Planner
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </section>

      <section className="bg-ivory border-y border-line" id="how">
        <div className="container-nc py-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl w-full aspect-[3/4] overflow-hidden border border-line shadow-soft bg-cream max-w-md">
            <PlannerPagePreview page={featuredPage} />
          </div>
          <div>
            <p className="overline mb-4">Featured spread</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
              Weekly Reset.
            </h2>
            <p className="mt-5 text-muted_brown leading-relaxed">
              A clean weekly reset page to review what happened, what went well, what
              felt heavy, and what needs to change before the next week begins.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-espresso">
              <li>- What happened this week</li>
              <li>- What went well</li>
              <li>- What felt heavy</li>
              <li>- One promise for next week</li>
            </ul>
            <Link to="/product/life-os-planner" className="btn-primary mt-8">
              View Planner <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-nc py-20">
        <div className="max-w-3xl mx-auto">
          <p className="overline mb-4 text-center">Frequently asked</p>
          <h2 className="font-serif text-4xl text-espresso text-center mb-10">
            Questions answered.
          </h2>
          <Accordion type="single" collapsible>
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-line">
                <AccordionTrigger className="text-espresso text-left" data-testid={`inside-faq-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted_brown">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
